import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { CreateLocationRequest } from '@/definitions/location'
import { CreateLocation } from '@/services/LocationService'
import { MINIMUM_PROVISIONING_MS, withMinimumDuration } from '@/utils/minimumDuration'

interface CreateLocationVariables {
  orgId: string
  data: CreateLocationRequest
}

export const useCreateLocation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    // The floor is held here rather than at the call site so the mutation's own
    // pending state covers the whole wait a caller is showing progress for.
    mutationFn: ({ orgId, data }: CreateLocationVariables) =>
      withMinimumDuration(CreateLocation(orgId, data), MINIMUM_PROVISIONING_MS),
    // The setup flow renders this failure inline because it has to say that the
    // organization itself survived — context a generic toast cannot carry. Any
    // caller added outside that flow should report the error themselves.
    meta: { suppressGlobalError: true },
    onSuccess: (_location, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['locations', orgId] })
      // A created location is always primary, so the backend repoints the
      // organization's primary_location_id and the cached organization is stale.
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] })
    },
  })
}
